import { useEffect, useMemo, useState } from 'react';
import type React from 'react';

import type { TransitionStatus } from 'react-transition-state';
import { useTransitionMap } from 'react-transition-state';

import type { HeroFeedItem as HeroFeedItemType } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

import { HeroFeedItem } from './components/HeroFeedItem';
import { useHeroFeed } from './hooks/useHeroFeed';

type Props = {
	items: readonly HeroFeedItemType[];
	intervalSeconds?: number;
};

const DEFAULT_INTERVAL_SECONDS = 3;

const HeroFeedDesktop: React.FC<{
	displayedItems: readonly HeroFeedItemType[];
	animateSpeed: number;
	animateDelay: number;
}> = ({ displayedItems, animateSpeed, animateDelay }) => {
	const { stateMap, setItem, toggle } = useTransitionMap<string>({
		timeout: animateDelay + animateSpeed,
		unmountOnExit: true,
	});

	// Keep a map of items by ID so we can render exiting items
	const [itemsMap, setItemsMap] = useState<Map<string, HeroFeedItemType>>(() => new Map());

	useEffect(() => {
		const currentIds = new Set(displayedItems.map((item) => item.userId));

		// Update items map with new items
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setItemsMap((prev) => {
			const next = new Map(prev);
			for (const item of displayedItems) {
				next.set(item.userId, item);
			}
			return next;
		});

		// Add new items to transition map
		for (const item of displayedItems) {
			if (!stateMap.has(item.userId)) {
				setItem(item.userId, { initialEntered: true });
			}
		}

		// Trigger exit animation for items that are no longer in the list
		for (const [id, state] of stateMap) {
			if (!currentIds.has(id) && state.isEnter) {
				toggle(id, false);
			}
		}
	}, [displayedItems, setItem, toggle, stateMap]);

	// Build the list of items to render (includes exiting items)
	const itemsToRender = useMemo(() => {
		const result: Array<{ id: string; item: HeroFeedItemType; status: TransitionStatus }> = [];

		// First, add items from stateMap (includes exiting items)
		for (const [id, state] of stateMap) {
			if (state.status !== 'unmounted') {
				const item = itemsMap.get(id);
				if (item) {
					result.push({ id, item, status: state.status });
				}
			}
		}

		// On first render, stateMap is empty - render displayedItems directly as "entered"
		if (result.length === 0) {
			for (const item of displayedItems) {
				result.push({ id: item.userId, item, status: 'entered' });
			}
		}

		return result;
	}, [stateMap, itemsMap, displayedItems]);

	return (
		<div className="relative overflow-hidden whitespace-nowrap" data-testid="hero-feed">
			<div role="list" className="size-full overflow-hidden">
				{itemsToRender.map(({ id, item, status }) => (
					<HeroFeedItem
						key={id}
						role="listitem"
						item={item}
						animateDelay={animateDelay}
						animateSpeed={animateSpeed}
						status={status}
					/>
				))}
			</div>
		</div>
	);
};

const A11yControls: React.FC<{
	a11y: {
		label: string;
		buttonLabel: string;
		isFeedAccessible: boolean;
	};
	toggleFeedAccessibility: () => void;
}> = ({ a11y, toggleFeedAccessibility }) => {
	return (
		<>
			<span className="sr-only">{a11y.label}</span>
			<button
				type="button"
				className="sr-only"
				data-qa="hero-feed-toggle-list"
				aria-expanded={a11y.isFeedAccessible}
				aria-controls="hero-feed-accessible-list"
				tabIndex={-1}
				onClick={toggleFeedAccessibility}
			>
				{a11y.buttonLabel}
			</button>
		</>
	);
};

export const HeroFeed: React.FC<Props> = ({ items, intervalSeconds = DEFAULT_INTERVAL_SECONDS }) => {
	const isMobile = useIsMobile();
	const mode = isMobile ? 'mobile' : 'desktop';
	const {
		scrollerRef,
		reversedItems,
		data: { displayedItems, animateSpeed, animateDelay, a11y },
		actions: { toggleFeedAccessibility },
	} = useHeroFeed(items, intervalSeconds, mode);

	if (isMobile) {
		return (
			<>
				<A11yControls a11y={a11y} toggleFeedAccessibility={toggleFeedAccessibility} />
				<div
					aria-hidden={!a11y.isFeedAccessible}
					id="hero-feed-accessible-list"
					className="flex max-h-20 w-full flex-col gap-2 overflow-hidden"
					ref={scrollerRef}
				>
					{reversedItems.map((item) => (
						<HeroFeedItem key={item.userId} item={item} />
					))}
				</div>
			</>
		);
	}

	if (!displayedItems.length) return null;

	return (
		<>
			<A11yControls a11y={a11y} toggleFeedAccessibility={toggleFeedAccessibility} />
			<div aria-hidden={!a11y.isFeedAccessible} id="hero-feed-accessible-list">
				<HeroFeedDesktop displayedItems={displayedItems} animateSpeed={animateSpeed} animateDelay={animateDelay} />
			</div>
		</>
	);
};
